/**
 * MagicRewrite — Iter 208
 * -----------------------
 * A flash-icon button + inline preview panel that rewrites the current
 * staff draft using the Rigo LLM (backend: POST /api/ai/rewrite-reply).
 *
 * Design:
 *  - Small icon button (Lightning) sits inside the composer alongside
 *    the template picker & send button.
 *  - Clicking pops a preview panel above the composer with:
 *      - Tone dropdown (professional / empathetic / formal-apologetic
 *        / concise / friendly).
 *      - Original draft (read-only) and Rewritten preview (read-only).
 *      - "Accept" replaces the draft with the rewrite.
 *      - "Reject" closes the panel; draft is untouched.
 *      - "Regenerate" re-hits the endpoint with the currently-selected
 *        tone.
 *
 * Props:
 *  - draft            : current textarea value
 *  - onAccept(text)   : caller updates the textarea state
 *  - testidPrefix     : namespace for data-testids (e.g. "ticket" | "chat")
 *  - size             : "sm" (h-9 w-9, for tighter ticket modal) | "md" (h-10 w-10, chat)
 */
import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Lightning, CircleNotch, CheckCircle, X, ArrowsClockwise } from "@phosphor-icons/react";

const TONES = [
  { key: "professional",      label: "Professional" },
  { key: "empathetic",        label: "Empathetic" },
  { key: "formal_apologetic", label: "Formal + Apologetic" },
  { key: "concise",           label: "Concise" },
  { key: "friendly",          label: "Friendly" },
];

export function MagicRewrite({ draft, onAccept, testidPrefix = "rewrite", size = "md" }) {
  const [open, setOpen]         = useState(false);
  const [tone, setTone]         = useState("professional");
  const [busy, setBusy]         = useState(false);
  const [preview, setPreview]   = useState("");
  const [srcDraft, setSrcDraft] = useState("");

  const btnClass = size === "sm"
    ? "h-9 w-9"
    : "h-10 w-10";

  const runRewrite = async (nextTone) => {
    const text = (draft || "").trim();
    if (!text) {
      toast.error("Type a rough reply first, then click Magic Rewrite.");
      return;
    }
    setBusy(true);
    setSrcDraft(text);
    try {
      const r = await api.post("/ai/rewrite-reply", { text, tone: nextTone || tone });
      setPreview((r.data?.rewritten || "").trim());
      setOpen(true);
    } catch (e) {
      toast.error(formatApiError(e) || "Rewrite failed — please try again");
    } finally {
      setBusy(false);
    }
  };

  const onToneChange = (t) => {
    setTone(t);
    // If preview is already open, re-run for the new tone.
    if (open) runRewrite(t);
  };

  const accept = () => {
    if (preview) onAccept(preview);
    setOpen(false);
    setPreview("");
  };

  const reject = () => {
    setOpen(false);
    setPreview("");
  };

  return (
    <>
      <button type="button" onClick={() => runRewrite()} disabled={busy}
              data-testid={`${testidPrefix}-magic-rewrite-btn`}
              title="Magic Rewrite — rephrase into professional customer-care tone"
              className={`${btnClass} rounded-full border border-[#B8860B] text-[#8C6108] hover:bg-[#FDF3D0] disabled:opacity-60 flex items-center justify-center shrink-0 transition-colors`}>
        {busy
          ? <CircleNotch size={16} className="animate-spin"/>
          : <Lightning size={16} weight="fill"/>}
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
             data-testid={`${testidPrefix}-magic-rewrite-panel`}
             onClick={reject}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E5E2D9]">
              <Lightning size={18} weight="fill" className="text-[#B8860B]"/>
              <div className="text-sm font-semibold text-[#2D3033]">Magic Rewrite</div>
              <div className="ml-auto flex items-center gap-2">
                <select value={tone} onChange={(e) => onToneChange(e.target.value)}
                        disabled={busy}
                        data-testid={`${testidPrefix}-magic-rewrite-tone`}
                        className="h-8 text-xs border border-[#E5E2D9] rounded px-2 outline-none">
                  {TONES.map(t => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
                <button type="button" onClick={reject}
                        data-testid={`${testidPrefix}-magic-rewrite-close`}
                        className="h-8 w-8 rounded-full hover:bg-[#F5F3EC] flex items-center justify-center text-[#5C5A55]">
                  <X size={14}/>
                </button>
              </div>
            </div>

            <div className="px-4 py-3 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] mb-1">Your draft</div>
                <div className="text-[12px] text-[#5C5A55] bg-[#FAFAF7] border border-[#EFECE3] rounded-md p-2 whitespace-pre-wrap"
                     data-testid={`${testidPrefix}-magic-rewrite-src`}>
                  {srcDraft || "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] mb-1 flex items-center gap-1">
                  Rigo suggestion
                  {busy && <CircleNotch size={10} className="animate-spin"/>}
                </div>
                <div className="text-[13px] text-[#2D3033] bg-[#FDF9E8] border border-[#EFDCA1] rounded-md p-2 whitespace-pre-wrap min-h-[80px]"
                     data-testid={`${testidPrefix}-magic-rewrite-preview`}>
                  {preview || (busy ? "Rewriting…" : "—")}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 px-4 py-3 border-t border-[#E5E2D9]">
              <button type="button" onClick={() => runRewrite()} disabled={busy}
                      data-testid={`${testidPrefix}-magic-rewrite-regen`}
                      className="h-9 px-3 rounded-md text-[#5C5A55] hover:bg-[#F5F3EC] text-xs inline-flex items-center gap-1 disabled:opacity-60">
                <ArrowsClockwise size={12}/> Regenerate
              </button>
              <div className="flex-1"/>
              <button type="button" onClick={reject}
                      data-testid={`${testidPrefix}-magic-rewrite-reject`}
                      className="h-9 px-3 rounded-md border border-[#E5E2D9] hover:bg-[#F5F3EC] text-xs">
                Reject
              </button>
              <button type="button" onClick={accept} disabled={busy || !preview}
                      data-testid={`${testidPrefix}-magic-rewrite-accept`}
                      className="h-9 px-4 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] text-white text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
                <CheckCircle size={12} weight="fill"/> Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MagicRewrite;
